import { memo } from 'react';
import { Link } from 'react-router-dom';
import parse, { domToReact, type DOMNode } from 'html-react-parser';

import { type ViewColumnConnSeparator } from '@/types/schema/LiveAppView';
import { useApplicationQuery } from '@/hooks/api/queries/useApplicationQuery';
import { type ViewRecord } from '@/hooks/api/queries/useViewRecordQuery';
import { usePageSegmentsHelpers } from '@/hooks/helpers/usePageSegmentsHelpers';
import { isPageEditor } from '@/utils/iframe';
import { cn } from '@/utils/tailwind';

interface ViewRecordInputValueRenderProps {
  recordValue: ViewRecord[string];
  connLink?: string;
  connSeparator?: ViewColumnConnSeparator;
}

// Parse an HTML string and convert it into React nodes
function ParsedHtml({ html, connLinkBasePath }: { html: string; connLinkBasePath?: string }) {
  return parse(html, {
    replace: (domNode) => {
      // Convert any internal record connection links to React Router Links.
      // Connection links are identified by a specific data attribute (data-kn="connection-link")
      if (
        domNode.type === 'tag' &&
        domNode.name === 'a' &&
        domNode.attribs?.['data-kn'] === 'connection-link'
      ) {
        const children = domNode.children as DOMNode[];
        const href = domNode.attribs?.href;
        const recordId = href?.replace('#', '');

        // If we are in the page editor and there is no connection link (e.g. the setting is toggled off), we just return the children as is. This allows the editor to display the connection value without link styles
        if (!connLinkBasePath && isPageEditor()) {
          return domToReact(children);
        }

        // Find the connection value element within the anchor link and apply the desired link classes
        const modifiedChildren = children.map((child) => {
          // Connection values are identified by a specific data attribute (data-kn="connection-value")
          if (
            child.type === 'tag' &&
            child.name === 'span' &&
            child.attribs?.['data-kn'] === 'connection-value'
          ) {
            const { id, class: className, 'data-kn': dataKn, ...restAttribs } = child.attribs;

            return {
              ...child,
              attribs: {
                id,
                'data-kn': dataKn,
                class: cn(className, 'inline-block text-brand-default underline'),
                ...restAttribs
              }
            };
          }

          return child;
        }) as DOMNode[];

        return <Link to={`${connLinkBasePath}/${recordId}`}>{domToReact(modifiedChildren)}</Link>;
      }

      // If the node is the connection value span, we want to apply the appropriate styles.
      // Connection values are identified by a specific data attribute (data-kn="connection-value")
      if (
        domNode.type === 'tag' &&
        domNode.name === 'span' &&
        domNode.attribs?.['data-kn'] === 'connection-value'
      ) {
        const { id, class: existingClassName, 'data-kn': dataKn, ...restAttribs } = domNode.attribs;

        return (
          <span
            id={id}
            data-kn={dataKn}
            className={cn(existingClassName, 'inline-block', {
              // This extra check is to ensure that if we are in the page editor and the user toggles on the connection link setting, we apply the link styles to the connection value, since it isn't a link yet
              'text-brand-default underline': isPageEditor() && connLinkBasePath
            })}
            {...restAttribs}
          >
            {domToReact(domNode.children as DOMNode[])}
          </span>
        );
      }

      return domNode;
    }
  });
}

const ParsedHtmlMemoized = memo(ParsedHtml);

export function ViewRecordInputValueRender({
  recordValue,
  connLink,
  connSeparator
}: ViewRecordInputValueRenderProps) {
  const { data: application } = useApplicationQuery();
  const { getPagePathFromSlug } = usePageSegmentsHelpers();

  // At this point, the recordValue should be a string since it should have been sanitized already. Still, we handle cases where it might not be a string to avoid runtime errors.
  if (typeof recordValue !== 'string') {
    if (typeof recordValue === 'number' || typeof recordValue === 'boolean') {
      return String(recordValue);
    }
    return null;
  }

  const connLinkPage = connLink
    ? application?.pages.find((page) => page.key === connLink)
    : undefined;
  const connLinkBasePath = connLinkPage ? getPagePathFromSlug(connLinkPage.slug) : undefined;

  const getFormattedValue = () => {
    let formattedValue = recordValue;

    if (recordValue.includes('data-kn="connection-value"')) {
      // If the record value contains connection values, we want to remove any trailing commas or line breaks
      formattedValue = formattedValue.replace(/,\s*$|<br\s*\/?>\s*$/g, '');
    }

    // If not in the page editor, there is nothing more to format
    if (!isPageEditor()) {
      return formattedValue;
    }

    // If we are in the page editor, we want to reactively convert between comma-separated and new line formats based on the separator
    if (connSeparator === 'new_line' || connSeparator === '') {
      // Convert comma-separated HTML elements into line-break-separated elements
      return formattedValue.replace(/>, </g, '><br><');
    }
    if (connSeparator === 'comma') {
      //  Convert line-break-separated HTML elements into comma-separated elements
      return formattedValue.replace(/><br><|><br> </g, '>, <');
    }

    return formattedValue;
  };

  return <ParsedHtmlMemoized html={getFormattedValue()} connLinkBasePath={connLinkBasePath} />;
}
